import { motion } from "framer-motion";
import { Heart, Users, ArrowRight, Music4, Disc3, Sparkles } from "lucide-react";
import { SITE } from "../data/site";

const SOCIAL_STYLES = [
  { name: "Salsa", vibe: "Cuban & Colombian", icon: Disc3 },
  { name: "Bachata", vibe: "Sensual flow", icon: Heart },
  { name: "Kizomba", vibe: "Slow & close", icon: Heart },
  { name: "Zouk", vibe: "French Caribbean", icon: Music4 },
  { name: "Kompa", vibe: "Haitian rhythm", icon: Music4 },
  { name: "Afro Socials", vibe: "Afrobeats / Amapiano", icon: Sparkles },
];

export const Workshops = () => {
  return (
    <section
      id="workshops"
      data-testid="workshops-section"
      className="relative py-24 sm:py-32 overflow-hidden bg-[#0b0410]"
    >
      {/* Nightclub ambient lighting */}
      <div className="absolute -top-20 left-1/4 w-[600px] h-[600px] rounded-full bg-brand-purple/35 blur-[180px]" />
      <div className="absolute top-1/3 -right-32 w-[500px] h-[500px] rounded-full bg-fuchsia-700/25 blur-[160px]" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-brand-yellow/12 blur-[170px]" />

      {/* Animated club beam */}
      <motion.div
        aria-hidden
        className="absolute top-0 left-1/3 w-1 h-full pointer-events-none"
        style={{
          background: "linear-gradient(to bottom, rgba(255,218,31,0.35), transparent)",
          transformOrigin: "top center",
        }}
        animate={{ rotate: [-12, 12, -12], opacity: [0.2, 0.6, 0.2] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute top-0 right-1/3 w-1 h-full pointer-events-none"
        style={{
          background: "linear-gradient(to bottom, rgba(139,92,246,0.4), transparent)",
          transformOrigin: "top center",
        }}
        animate={{ rotate: [10, -14, 10], opacity: [0.3, 0.7, 0.3] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      {/* Grain */}
      <div className="absolute inset-0 noise opacity-80 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        {/* Warm intro header */}
        <div className="max-w-3xl mb-14">
          <div className="flex items-center gap-3 mb-5">
            <Disc3 size={22} className="text-brand-yellow animate-spin" style={{ animationDuration: "6s" }} />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">02 · Monthly Workshops & Community Gatherings</span>
          </div>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-wide leading-[0.95]">
            After dark. <span className="italic text-brand-yellow text-glow-yellow">After hours.</span>
            <br /> Together on the <span className="text-brand-purpleLight">floor</span>.
          </h2>
          <p className="mt-6 text-zinc-300/90 text-base sm:text-lg max-w-2xl leading-relaxed">
            Once a month, USDD opens the doors to cultural workshops, social
            nights and community gatherings — designed to bring people together
            through movement, music and culture.
          </p>
        </div>

        {/* Layout: hero feature card + social style strip */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
          {/* Featured: Community Dance Nights — warm nightlife card */}
          <motion.div
            data-testid="community-feature-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-8 relative overflow-hidden rounded-[2rem] min-h-[460px] group"
            style={{
              boxShadow: "0 30px 80px -20px rgba(90,24,154,0.5), 0 0 40px rgba(255,218,31,0.08) inset",
            }}
          >
            <img
              src={SITE.socialImg1}
              alt="Community dance night"
              className="absolute inset-0 w-full h-full object-cover brightness-[0.4] saturate-150 group-hover:brightness-55 transition-all duration-700"
              style={{ filter: "hue-rotate(-10deg) brightness(0.45) saturate(1.4)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#1a0530] via-brand-purple/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />

            {/* Soft scanline glow */}
            <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-brand-yellow/15 to-transparent mix-blend-screen" />

            <div className="relative h-full flex flex-col justify-between p-8 sm:p-12">
              <div className="flex flex-wrap items-start gap-3">
                <span className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.25em] text-black bg-brand-yellow px-4 py-2 rounded-full">
                  <Heart size={13} fill="currentColor" /> Free / Donation-based
                </span>
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-white border border-white/30 px-3 py-2 rounded-full bg-white/10 backdrop-blur">
                  Monthly · After Hours
                </span>
              </div>
              <div>
                <Disc3 className="text-brand-yellow mb-5 animate-spin" size={36} style={{ animationDuration: "8s" }} />
                <h3 className="font-display text-4xl sm:text-6xl text-white tracking-wide leading-[0.9]">
                  Community
                  <br />
                  <span className="text-brand-yellow text-glow-yellow italic">Dance Nights</span>
                </h3>
                <p className="mt-5 text-zinc-200/90 max-w-xl text-sm sm:text-base leading-relaxed">
                  Late-evening gatherings built around movement, music and connection.
                  Some sessions are offered free or donation-based as part of USDD's
                  community engagement programme — accessible movement for everyone.
                </p>
                <a
                  href="#contact"
                  data-testid="community-nights-cta"
                  className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white font-bold uppercase text-xs tracking-[0.2em] hover:bg-brand-yellow hover:text-black hover:border-brand-yellow transition-all"
                >
                  Get the next date <ArrowRight size={15} />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Right column: Cyphers + Afro Socials */}
          <div className="lg:col-span-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
            <motion.div
              data-testid="workshops-cyphers-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative overflow-hidden rounded-[2rem] min-h-[215px] group border border-brand-purpleLight/20"
            >
              <img src={SITE.socialImg3} alt="Open cypher" className="absolute inset-0 w-full h-full object-cover brightness-[0.4] saturate-150 group-hover:brightness-65 transition-all duration-500" />
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-purpleDeep via-brand-purple/20 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="relative h-full p-7 flex flex-col justify-end">
                <Sparkles className="text-brand-yellow mb-3" size={22} />
                <h4 className="font-display text-3xl text-white tracking-wide">Open Cyphers</h4>
                <p className="text-xs text-zinc-300/90 mt-1">Street freestyle circles — every style, every level.</p>
              </div>
            </motion.div>

            <motion.div
              data-testid="workshops-socials-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative overflow-hidden rounded-[2rem] min-h-[215px] group border border-brand-yellow/20"
            >
              <img src={SITE.socialImg2} alt="Afro socials" className="absolute inset-0 w-full h-full object-cover brightness-[0.4] saturate-150 group-hover:brightness-65 transition-all duration-500" />
              <div className="absolute inset-0 bg-gradient-to-tr from-orange-900/30 via-brand-purple/30 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="relative h-full p-7 flex flex-col justify-end">
                <Music4 className="text-brand-yellow mb-3" size={22} />
                <h4 className="font-display text-3xl text-white tracking-wide">Afro Socials</h4>
                <p className="text-xs text-zinc-300/90 mt-1">Afrobeats, Amapiano and Afro-house gatherings.</p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Social styles strip — warm, soft pills */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-5">Featuring</div>
          <div className="flex flex-wrap gap-3">
            {SOCIAL_STYLES.map((s, i) => (
              <motion.div
                key={s.name}
                data-testid={`workshop-style-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="group flex items-center gap-3 pl-4 pr-5 py-3 rounded-full bg-white/[0.04] border border-white/10 hover:border-brand-yellow/50 hover:bg-white/[0.08] transition-all"
              >
                <s.icon size={16} className="text-brand-yellow" />
                <span className="font-display text-xl text-white tracking-wide">{s.name}</span>
                <span className="h-3 w-px bg-white/15" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{s.vibe}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
